// LoginOverlay.tsx — farstars' sign-in panel, drawn over the galaxy.
//
// Everywhere else in the repo this is the shared `LoginForm` from lib/web,
// but that component is built on react-bootstrap and farstars ships none
// (DESIGN.md records the C-BOOT-1 exemption: nothing here renders Bootstrap,
// so the stylesheet would be dead weight over a full-bleed 3D canvas). The
// contract it implements is the part that matters, and that is shared: POST
// credentials to /api/oidc/password-login, take the session cookie off the
// response, carry on. Only the chrome is local, in farstars' own HUD idiom.
//
// When the backend can't take a password — embedded login not enabled for
// this client — the panel collapses to the sign-in button that was farstars'
// whole login flow before.
import { useState } from 'react';
import { loginRedirect, passwordLogin, type LoginCapabilities } from './api';

interface LoginOverlayProps {
  caps: LoginCapabilities;
  /** Re-run the session bootstrap once a cookie exists. */
  onSignedIn: () => void;
}

export function LoginOverlay({ caps, onSignedIn }: LoginOverlayProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await passwordLogin(email, password);
      setPassword('');
      onSignedIn();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Sign-in failed.');
      setBusy(false);
    }
  };

  return (
    <div className="login-overlay">
      <div className="login-panel">
        <div className="brand login-brand">
          <span className="brand-mark">✦</span>FARSTARS
        </div>

        {error && <div className="login-error">{error}</div>}

        {caps.password ? (
          <form className="login-form" onSubmit={submit}>
            <label className="login-label" htmlFor="farstars-email">Email</label>
            <input
              id="farstars-email"
              className="login-input"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <label className="login-label" htmlFor="farstars-password">Password</label>
            <input
              id="farstars-password"
              className="login-input"
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button className="login-btn login-btn-primary" type="submit" disabled={busy}>
              {busy ? 'Signing in…' : 'Sign In'}
            </button>
          </form>
        ) : null}

        {caps.password && caps.providers.length > 0 && <div className="login-divider">OR</div>}

        {caps.providers.map((p) => (
          <button
            key={p}
            className="login-btn"
            type="button"
            onClick={() => loginRedirect(p)}
          >
            Sign in with {p === 'github' ? 'GitHub' : 'Google'}
          </button>
        ))}

        {!caps.password && (
          <button className="login-btn login-btn-primary" type="button" onClick={() => loginRedirect()}>
            Sign in with {caps.issuer}
          </button>
        )}
      </div>
    </div>
  );
}

export default LoginOverlay;
